
import type { DailyBrowseData } from '@empathybroker/tsclient-stats/dist/models/daily-browse/daily-browse-data'
import type { StatsParams } from '@empathybroker/tsclient-stats/dist/models/params/stats-params'
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type { TopCategoriesCategoryItem } from '@empathybroker/tsclient-stats/dist/models/topreport/categories/data/topcategories-categoryitem'
import type { Params, TreemapConfig, TreemapData, TreemapDataItem } from '@empathyco/insights'
import type { DataTableColumn, Dictionary, Pagination } from '../models'
import { FormatEnum } from '@empathybroker/tsclient-stats/dist/enums/format.enum'
import { NavigationModeEnum } from '@empathybroker/tsclient-stats/dist/enums/navigation-mode.enum'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'

import { TreemapComponent } from '@empathyco/insights'
import { defineComponent } from 'vue'

import { mapGetters } from 'vuex'

import { EpbText } from '../components'
import DataTable from '../components/crud/data-table.component.vue'
import PaginationBar from '../components/crud/pagination-bar.component.vue'
import InsightsNoResults from '../components/insights/insights-no-results.component.vue'
import { UiTooltip } from '../components/keen-ui'
import { EpbBadge } from '../components/shared/epb-badge'
import { EpbButton } from '../components/shared/epb-button'
import { EpbToggle } from '../components/shared/epb-toggle'

import { useAppLoading } from '../composables/use-app-loading'

import { AlignEnum, CompareFilterEnum, FilterIdEnum, KpiEnum } from '../enums'

import { capitalizeSentence } from '../filters'
import { InsightsMixin } from '../mixins'

import { LoadDistDataAction } from '../store/analytics/actions/load-dist-data.action'
import { CONFIG_CONSTS } from '../store/config/config.consts'
import { CONTEXT_CONSTS } from '../store/context/context.consts'
import { DownloadUtils } from '../utils/download.utils'

interface Data {
  topCategories: TopCategoriesCategoryItem[]
  dailyBrowse: DailyBrowseData
  dailyBrowseToCompare?: DailyBrowseData
}

interface Categories {
  [kpi: string]: TopCategoriesCategoryItem[]
}

interface BrowseCategoryRow extends Record<string, unknown> {
  id: string
  categoryName: string
  level: number
  children?: BrowseCategoryRow[]
}

enum ModeEnum {
  COMPONENT = 'component',
  TABLE = 'table',
}

let JS_INTERVAL_REF

export default defineComponent({
  name: 'BrowseAnalytics',
  components: {
    EpbText,
    EpbToggle,
    EpbBadge,
    EpbButton,
    UiTooltip,
    DataTable,
    PaginationBar,
    InsightsNoResults,
  },
  mixins: [InsightsMixin],
  setup() {
    const { loading } = useAppLoading()
    return { loading }
  },
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      kpis: CONFIG_CONSTS.getters.browseKpis,
    }),
    ...mapGetters(CONTEXT_CONSTS.moduleName, {
      enabledFilters: CONTEXT_CONSTS.getters.enabledFilters,
    }),
    modeOptions(): { id: string; content: string }[] {
      return Object.values(ModeEnum).map((id: ModeEnum) => ({
        id,
        content: {
          [ModeEnum.COMPONENT]: 'treemap',
          [ModeEnum.TABLE]: 'list',
        }[id],
      }))
    },
    _kpis(): KpiEnum[] {
      return [
        KpiEnum.VIEW,
        KpiEnum.POSITION_AVG,
        KpiEnum.CLICKS,
        KpiEnum.FINDABILITY,
        KpiEnum.ADD2CART,
        KpiEnum.CHECKOUT,
      ].filter((kpi: KpiEnum) => this.kpis.includes(kpi))
    },
    isComponentMode(): boolean {
      return this.mode === ModeEnum.COMPONENT
    },
    hasDailyBrowseToCompare(): boolean {
      return !!this.data?.dailyBrowseToCompare
    },
    rootCategoriesForTreemap(): TopCategoriesCategoryItem[] {
      if (this.drillDownParent) {
        return this.currentViewCategories
      }
      return this.currentViewCategories.filter(
        (item: TopCategoriesCategoryItem) => (item.level ?? 1) === 1,
      )
    },
    categories(): Categories {
      const categories: Categories = {}
      if (this.data || this.drillDownChildren.length) {
        this._kpis.forEach((kpi: KpiEnum) => {
          const kpiField = this.getKpiField(kpi)
          categories[kpi] = this.getCategoriesByField(kpiField, this.rootCategoriesForTreemap)
        })
      }
      return categories
    },
    componentParams(): Params<TreemapConfig, TreemapData> {
      return {
        config: {
          padding: 7,
          minOpacity: 0.04,
          maxOpacity: 0.4,
          valueSuffix: this.getKpiValueSuffix(this.selectedKpi),
        },
        data: this.componentData,
      }
    },
    componentData(): TreemapData {
      if (!Object.entries(this.categories).length || !this.selectedKpi) {
        return []
      }
      return this.categories[this.selectedKpi].map(this.getComponentDataItemFromCategory)
    },
    tableColumns(): DataTableColumn[] {
      const resultColumns: DataTableColumn[] = []
      resultColumns.push({
        id: 'categoryName',
        text: capitalizeSentence(this.messages.categoryLabel),
      })
      const kpiColumns = this._kpis.reduce((columns: DataTableColumn[], kpi: KpiEnum) => {
        columns.push({
          id: this.getKpiField(kpi),
          text: capitalizeSentence(this.getKpiLabel(kpi)),
          align: AlignEnum.END,
        })
        return columns
      }, [])
      resultColumns.push(...kpiColumns)
      return resultColumns
    },
    categoryTreeRoots(): BrowseCategoryRow[] {
      return this.buildCategoryTree(this.currentViewCategories)
    },
    tableData(): BrowseCategoryRow[] {
      const roots = this.categoryTreeRoots
      if (!roots.length) {
        return []
      }
      const offset = this.getPaginationData(this.pagination).offset
      const paginatedRoots = roots.slice(offset, offset + this.pagination.itemsPerPage)
      return paginatedRoots.map((node: BrowseCategoryRow) => this.mapTreeNodeToRow(node))
    },
    currentViewCategories(): TopCategoriesCategoryItem[] {
      if (this.drillDownParent) {
        return this.drillDownChildren
      }
      return this.data?.topCategories ?? []
    },
    topCategoriesByPagination(): TopCategoriesCategoryItem[] {
      if (!this.currentViewCategories.length) {
        return []
      }
      const offset = this.getPaginationData(this.pagination).offset
      return this.currentViewCategories.slice(offset, offset + this.pagination.itemsPerPage)
    },
    hasNoResults(): boolean {
      return !this.loading && !this.data?.dailyBrowse.viewCount
    },
  },
  data() {
    const itemsPerPageOptions = [10, 50, 100, 200]
    return {
      baseClass: 'epb-browse-analytics',
      mode: ModeEnum.COMPONENT,
      maxCategoriesCount: 20,
      selectedKpi: KpiEnum.VIEW,
      component: undefined,
      drillDownParent: null as TopCategoriesCategoryItem | null,
      drillDownChildren: [] as TopCategoriesCategoryItem[],
      pagination: {
        page: 1,
        itemsPerPage: itemsPerPageOptions[0],
        totalItems: 0,
      },
      itemsPerPageOptions,
      expandedChildrenIds: [] as string[],
      messages: this.$epb.messages.browseAnalytics,
    }
  },
  watch: {
    data(value: Data) {
      if (!value.topCategories.length) {
        this.changeMode(ModeEnum.TABLE)
      } else {
        this.expandedChildrenIds = []
      }
    },
    currentViewCategories(): void {
      if (!this.drillDownParent) {
        this.pagination.totalItems = this.categoryTreeRoots.length
      }
    },
    componentParams: {
      handler(params: Params<TreemapConfig, TreemapData>): void {
        this.component.update(params)
      },
      deep: true,
    },
  },
  created(): void {
    this.component = new TreemapComponent()
  },
  mounted(): void {
    this.component.init(this.$refs.component, this.componentParams)
    JS_INTERVAL_REF = setInterval(() => {
      const currentSelectedIndex: number = this._kpis.indexOf(this.selectedKpi)
      if (currentSelectedIndex === this._kpis.length - 1) {
        this.setSelectedKpi(this._kpis[0], true)
      } else {
        this.setSelectedKpi(this._kpis[currentSelectedIndex + 1])
      }
    }, 1500)
  },
  beforeDestroy(): void {
    this.component.destroy()
  },
  methods: {
    changeMode(mode: ModeEnum): void {
      clearInterval(JS_INTERVAL_REF)
      this.mode = mode
      if (this.isComponentMode) {
        this.selectedKpi = KpiEnum.VIEW
      } else {
        this.selectedKpi = undefined
        this.pagination.page = 1
        this.pagination.itemsPerPage = this.itemsPerPageOptions[0]
      }
    },
    getBadgeClasses(kpi: KpiEnum): (string | Record<string, boolean>)[] {
      return [
        `${this.baseClass}__badge`,
        {
          [`${this.baseClass}__badge--selected ${this.baseClass}__badge--${kpi}`]:
            kpi === this.selectedKpi,
        },
      ]
    },
    setSelectedKpi(kpi: KpiEnum, finishInitialAnimation = false): void {
      this.mode = ModeEnum.COMPONENT
      this.selectedKpi = kpi
      if (finishInitialAnimation) {
        clearInterval(JS_INTERVAL_REF)
      }
    },
    getKpiIcon(kpi: KpiEnum): string {
      return {
        [KpiEnum.VIEW]: 'show',
        [KpiEnum.POSITION_AVG]: 'hash',
        [KpiEnum.CLICKS]: 'actions',
        [KpiEnum.FINDABILITY]: 'findability',
        [KpiEnum.ADD2CART]: 'add2cart',
        [KpiEnum.CHECKOUT]: 'checkout',
      }[kpi]
    },
    getKpiCount(kpi: KpiEnum): string {
      if (!this.data?.dailyBrowse) {
        return
      }
      const count = this.getKpiRawValue(kpi)
      const formattedCount = this.formatNumber(count)
      return `${formattedCount}${this.getKpiValueSuffix(kpi)}`
    },
    getPreviousKpiCount(kpi: KpiEnum): number | undefined {
      if (!this.data?.dailyBrowseToCompare) {
        return
      }
      const previousDailyBrowse = this.data.dailyBrowseToCompare
      return {
        [KpiEnum.VIEW]: previousDailyBrowse.viewCount,
        [KpiEnum.POSITION_AVG]: previousDailyBrowse.positionAvg,
        [KpiEnum.CLICKS]: previousDailyBrowse.clickCountPct,
        [KpiEnum.FINDABILITY]: previousDailyBrowse.successCountPct,
        [KpiEnum.ADD2CART]: previousDailyBrowse.add2cartCountPct,
        [KpiEnum.CHECKOUT]: previousDailyBrowse.checkoutCountPct,
      }[kpi]
    },
    getKpiDiff(kpi: KpiEnum): string {
      const current = this.getKpiRawValue(kpi)
      const previous = this.getPreviousKpiCount(kpi)
      if (current === undefined || previous === undefined) {
        return ''
      }
      let diff = 0
      if (kpi === KpiEnum.VIEW || kpi === KpiEnum.POSITION_AVG) {
        diff = previous !== 0 ? ((current - previous) / Math.abs(previous)) * 100 : 0
      } else {
        diff = current - previous
      }
      const sign = diff > 0 ? '+' : diff < 0 ? '-' : ''
      const formattedDiff = this.formatNumber(Math.abs(diff))
      return `${sign}${formattedDiff}${this.getKpiValueSuffix(kpi)}${kpi === KpiEnum.VIEW || kpi === KpiEnum.POSITION_AVG ? '%' : ''}`
    },
    getKpiRawValue(kpi: KpiEnum): number | undefined {
      if (!this.data?.dailyBrowse) {
        return
      }
      const dailyBrowseData = this.data.dailyBrowse
      return {
        [KpiEnum.VIEW]: dailyBrowseData.viewCount,
        [KpiEnum.POSITION_AVG]: dailyBrowseData.positionAvg,
        [KpiEnum.CLICKS]: dailyBrowseData.clickCountPct,
        [KpiEnum.FINDABILITY]: dailyBrowseData.successCountPct,
        [KpiEnum.ADD2CART]: dailyBrowseData.add2cartCountPct,
        [KpiEnum.CHECKOUT]: dailyBrowseData.checkoutCountPct,
      }[kpi]
    },
    formatNumber(value: number): string {
      return this.$epb.utils.formatNumber(value, { maximumFractionDigits: 2 }, true)
    },
    getKpiValueSuffix(kpi: KpiEnum): string {
      if (kpi !== KpiEnum.VIEW && kpi !== KpiEnum.POSITION_AVG) {
        return '%'
      }
      return ''
    },
    getKpiLabel(kpi: KpiEnum): string {
      const kpiMessages = this.messages.kpis[kpi]
      const labelOptions = {
        [KpiEnum.VIEW]: kpiMessages.valueLabel,
        [KpiEnum.POSITION_AVG]: kpiMessages.valueLabel,
        default: () => kpiMessages.percentageLabel,
      }
      return labelOptions[kpi] || labelOptions.default()
    },
    onEnter(): void {
      if (this.isComponentMode) {
        this.component.init(this.$refs.component, this.componentParams)
      } else {
        this.component.destroy()
      }
    },
    getPaginationData(pagination: Pagination): { offset: number; rows: number } {
      return {
        offset: (this.pagination.page - 1) * this.pagination.itemsPerPage,
        rows: pagination.itemsPerPage,
      }
    },
    onPaginationChange(pagination: Pagination): void {
      this.pagination = pagination
    },
    getDateFilterRange(): { startDate: Date; endDate: Date } | undefined {
      const dateFilter = this.enabledFilters?.[FilterIdEnum.DATE]
      if (!dateFilter?.selectedValues?.length) {
        return
      }
      const [startDate, endDate] = dateFilter.selectedValues.map(
        (dateMilliseconds: number) => new Date(dateMilliseconds),
      )
      return { startDate, endDate }
    },
    getPeriodToCompareParams(periodToCompare: CompareFilterEnum): StatsParams | undefined {
      const dateRange = this.getDateFilterRange()
      if (!dateRange) {
        return
      }
      const [startDate, endDate] = LoadDistDataAction.getPeriodToCompareDates(periodToCompare)
      return {
        startDate,
        endDate,
      }
    },
    compareArrowIconClasses(value: string): string {
      let classes = 'epb-browse-analytics__icon eicon'
      if (!value.includes('-')) {
        classes += ' eicon-arrow-up epb-browse-analytics__icon--positive'
      } else {
        classes += ' eicon-arrow-down epb-browse-analytics__icon--negative'
      }
      return classes
    },
    async onDownload(pagination: Pagination): Promise<void> {
      try {
        const data = this.currentViewCategories
        const fileName = this.getDownloadFileName()
        DownloadUtils.downloadCSV(data, fileName)
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      }
    },
    getDownloadFileName(): string {
      const dateFilter = this.enabledFilters?.[FilterIdEnum.DATE]
      if (!dateFilter?.selectedValues?.length) {
        return 'Navigation'
      }
      const [startDate, endDate] = dateFilter.selectedValues.map(
        (dateMilliseconds: number) => new Date(dateMilliseconds),
      )
      const isSameYear = startDate.getFullYear() === endDate.getFullYear()
      const startDateString = this.getDownloadFileDateString(startDate, !isSameYear)
      const endDateString = this.getDownloadFileDateString(endDate, true)
      return `${startDateString}-${endDateString}_Navigation`
    },
    getDownloadFileDateString(date: Date, concatYear: boolean): string {
      const dateMonthString = `0${date.getMonth() + 1}`.slice(-2)
      const dateDayString = `0${date.getDate()}`.slice(-2)
      let dateString = `${dateMonthString}${dateDayString}`
      if (concatYear) {
        dateString = `${dateString}${date.getFullYear().toString().slice(2)}`
      }
      return dateString
    },
    async fetchData(): Promise<Data> {
      this.drillDownParent = null
      this.drillDownChildren = []
      const dateRange = this.getDateFilterRange()
      const dailyBrowseParams: StatsParams = dateRange
        ? {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }
        : {}
      const dailyBrowsePromise: Promise<StatsResponse> = this.fetchStatsReport({
        id: StatsReportsIdsEnum.DAILY_BROWSE,
        params: dailyBrowseParams,
      })
      let previousDailyBrowsePromise: Promise<StatsResponse> | undefined
      const dateCompare = this.enabledFilters?.[FilterIdEnum.COMPARE].selectedValues?.[0]
      if (dateRange) {
        let previousPeriodParams: StatsParams | null = null
        if (dateCompare === CompareFilterEnum.PREVIOUS_PERIOD) {
          previousPeriodParams = this.getPeriodToCompareParams(CompareFilterEnum.PREVIOUS_PERIOD)!
        } else if (dateCompare === CompareFilterEnum.YEAR_OVER_YEAR) {
          previousPeriodParams = this.getPeriodToCompareParams(CompareFilterEnum.YEAR_OVER_YEAR)!
        }
        previousDailyBrowsePromise = previousPeriodParams
          ? this.fetchStatsReport({
              id: StatsReportsIdsEnum.DAILY_BROWSE,
              params: previousPeriodParams,
            })
          : undefined
      }
      const topCategoriesPromise = this.fetchTopCategoriesForLevel(1, undefined, {
        offset: 0,
        rows: 0,
      })
      let dailyBrowseResponse, previousDailyBrowseResponse, topCategories

      if (
        dateCompare === CompareFilterEnum.PREVIOUS_PERIOD ||
        dateCompare === CompareFilterEnum.YEAR_OVER_YEAR
      ) {
        ;[dailyBrowseResponse, previousDailyBrowseResponse, topCategories] = await Promise.all([
          dailyBrowsePromise,
          previousDailyBrowsePromise,
          topCategoriesPromise,
        ])
      } else {
        ;[dailyBrowseResponse, topCategories] = await Promise.all([
          dailyBrowsePromise,
          topCategoriesPromise,
        ])
      }

      const allCategories = (topCategories as TopCategoriesCategoryItem[]) ?? []
      const rootCategories = allCategories.filter(
        (item: TopCategoriesCategoryItem) => (item.level ?? 1) === 1,
      )
      this.pagination.totalItems = rootCategories.length

      return {
        topCategories: allCategories,
        dailyBrowse: dailyBrowseResponse.data,
        ...(previousDailyBrowseResponse && {
          dailyBrowseToCompare: previousDailyBrowseResponse.data,
        }),
      }
    },
    async fetchTopCategoriesForLevel(
      level: number,
      parentId?: string,
      { offset, rows } = { offset: 0, rows: 0 },
    ): Promise<TopCategoriesCategoryItem[]> {
      const topCategoriesParams = {
        offset,
        rows,
        level,
        ...(parentId && { parentId }),
        navigationMode: NavigationModeEnum.NAVIGATION,
        format: FormatEnum.JSON,
      }
      const topCategoriesResponse: StatsResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.TOP_CATEGORIES,
        params: topCategoriesParams,
      })
      return topCategoriesResponse.data
    },
    onTreemapContainerClick(event: MouseEvent): void {
      // TODO: Prefer treemap emitting a click with category payload; DOM-based detection may break if treemap internals change.
      const target = event.target as HTMLElement
      const serie = target.closest?.('.ein-treemap__serie')
      if (!serie) return
      const datum = (serie as any).__data__
      if (!datum?.name) return
      const category = this.currentViewCategories.find(
        (c: TopCategoriesCategoryItem) => c.categoryName === datum.name,
      )
      if (!category) return
      this.onTreemapCategoryClick(category)
    },
    async onTreemapCategoryClick(category: TopCategoriesCategoryItem): Promise<void> {
      const childLevel = (category.level ?? 1) + 1
      const children = await this.fetchTopCategoriesForLevel(childLevel, category.categoryId, {
        offset: 0,
        rows: 0,
      })
      const parentIdToMatch = category.categoryId
      const actualChildren = (children as TopCategoriesCategoryItem[]).filter(
        (item: TopCategoriesCategoryItem) => {
          const levelMatch = (item.level ?? 1) === childLevel
          const itemParentId = (item as any).parent_id ?? (item as any).parentId ?? undefined
          const parentMatch = itemParentId === parentIdToMatch
          return levelMatch && parentMatch
        },
      )
      if (actualChildren.length === 0) return
      this.drillDownParent = category
      this.drillDownChildren = actualChildren
      this.pagination.page = 1
      this.pagination.totalItems = actualChildren.length
    },
    onDrillDownBack(): void {
      this.drillDownParent = null
      this.drillDownChildren = []
      this.pagination.page = 1
      this.pagination.totalItems = this.data?.topCategories?.length ?? 0
    },
    getKpiField(kpi: KpiEnum): string {
      return {
        [KpiEnum.VIEW]: 'categoryViewCount',
        [KpiEnum.POSITION_AVG]: 'positionAvg',
        [KpiEnum.CLICKS]: 'clickViewCountPct',
        [KpiEnum.FINDABILITY]: 'successViewCountPct',
        [KpiEnum.ADD2CART]: 'add2cartViewCountPct',
        [KpiEnum.CHECKOUT]: 'checkoutViewCountPct',
      }[kpi]
    },
    getCategoryId(item: TopCategoriesCategoryItem): string {
      return (item as any).category_id ?? item.categoryId
    },
    getParentId(item: TopCategoriesCategoryItem): string | undefined {
      return (item as any).parent_id ?? (item as any).parentId ?? undefined
    },
    getParentIdsFromCategories(categories: TopCategoriesCategoryItem[]): string[] {
      const parentIds = new Set<string>()
      categories.forEach((item: TopCategoriesCategoryItem) => {
        const parentId = this.getParentId(item)
        if (parentId) {
          parentIds.add(parentId)
        }
      })
      return Array.from(parentIds)
    },
    buildCategoryTree(categories: TopCategoriesCategoryItem[]): BrowseCategoryRow[] {
      if (!categories?.length) {
        return []
      }
      const itemLevel = (item: TopCategoriesCategoryItem) => item.level ?? 1
      const roots: BrowseCategoryRow[] = []
      const byId = new Map<string, BrowseCategoryRow>()

      categories.forEach((category: TopCategoriesCategoryItem) => {
        const id = this.getCategoryId(category)
        const kpisData = this._kpis.reduce((kpis: Dictionary<number>, kpi: KpiEnum) => {
          const kpiFieldName = this.getKpiField(kpi)
          kpis[kpiFieldName] = category[kpiFieldName]
          return kpis
        }, {})
        const node: BrowseCategoryRow = {
          id,
          categoryName: category.categoryName,
          level: itemLevel(category),
          ...kpisData,
          children: [],
        }
        byId.set(id, node)
      })

      categories.forEach((category: TopCategoriesCategoryItem) => {
        const id = this.getCategoryId(category)
        const parentId = this.getParentId(category)
        const node = byId.get(id)
        if (!node) return
        if (itemLevel(category) === 1 || !parentId) {
          roots.push(node)
        } else {
          const parent = byId.get(parentId)
          if (parent) {
            if (!parent.children) parent.children = []
            parent.children.push(node)
          } else {
            roots.push(node)
          }
        }
      })

      roots.sort((a, b) => (a.categoryName as string).localeCompare(b.categoryName as string))
      return roots
    },
    mapTreeNodeToRow(node: BrowseCategoryRow): BrowseCategoryRow {
      if (!node.children?.length) {
        const { children, ...rest } = node
        return { ...rest }
      }
      return {
        ...node,
        children: node.children.map((child: BrowseCategoryRow) => this.mapTreeNodeToRow(child)),
      }
    },
    getCategoriesByField(
      fieldName: string,
      source: TopCategoriesCategoryItem[] = this.currentViewCategories,
    ): TopCategoriesCategoryItem[] {
      return [...source]
        .sort(
          (category1: TopCategoriesCategoryItem, category2: TopCategoriesCategoryItem) =>
            category2[fieldName] - category1[fieldName],
        )
        .slice(0, this.maxCategoriesCount)
    },
    getComponentDataItemFromCategory(category: TopCategoriesCategoryItem): TreemapDataItem {
      const kpiField = this.getKpiField(this.selectedKpi)
      return {
        name: category.categoryName,
        value: category[kpiField],
        tooltip: this.getComponentDataItemTooltip(category),
      }
    },
    getComponentDataItemTooltip(category: TopCategoriesCategoryItem): string {
      const kpis = this._kpis.reduce((kpisTemplate: string, kpi: KpiEnum) => {
        const kpiField = this.getKpiField(kpi)
        const value = this.formatNumber(category[kpiField])
        const valueSuffix = this.getKpiValueSuffix(kpi)
        const kpiTemplate = `
            <div class="${this.baseClass}__tooltip-content-kpi-label">
              <span class="${this.baseClass}__tooltip-content-kpi-circle
                ${this.baseClass}__tooltip-content-kpi-circle--${kpi}">
              </span>
              <h5 class="${this.baseClass}__tooltip-content-kpi-name">
                ${this.getKpiLabel(kpi)}
              </h5>
            </div>
            <span class="${this.baseClass}__tooltip-content-kpi-value">
              ${value}${valueSuffix}
            </span>
          `
        return kpisTemplate.concat(kpiTemplate)
      }, '')
      return `
          <div class="${this.baseClass}__tooltip-content">
            <h5 class="${this.baseClass}__tooltip-content-category">
              ${category.categoryName}
            </h5>
            <div class="${this.baseClass}__tooltip-content-kpis">
              ${kpis}
            </div>
          </div>
        `
    },
  },
})
